import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  User,
  Mail,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Loader2,
  Search,
  MailOpen,
  History,
  Eye,
  Clock,
  Send,
  Tag,
  FileText,
  Link as LinkIcon,
  Upload,
  Download,
  CheckCircle2,
  AlertCircle,
  MoreVertical,
  Filter,
  Check,
  UserPlus
} from "lucide-react";
import Papa from "papaparse";
import { motion, AnimatePresence } from "framer-motion";
import EmailAnalyticsDashboard from "./EmailAnalyticsDashboard";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../../firebase.init";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";


const UserEmailTemplateManager = () => {
  const [user] = useAuthState(auth);
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({
    toEmail: "",
    subject: "",
    messageBody: "",
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedViewUser, setSelectedViewUser] = useState(null);
  const [formData, setFormData] = useState({
    userName: "",
    UserEmail: "",
    category: "forSubscriber",
    customFields: [],
  });
  const [categories, setCategories] = useState([
    "forSubscriber",
    "forRegister",
  ]);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importTab, setImportTab] = useState("csv"); // 'csv' or 'sheets'
  const [googleSheetLink, setGoogleSheetLink] = useState("");

  const itemsPerPage = 10;

  // Custom hook for outside click
  const useOutsideClick = (ref, callback) => {
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (ref.current && !ref.current.contains(event.target)) {
          callback();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref, callback]);
  };

  const getAvatarUrl = (name, email) => {
    const seed = name || email || "User";
    return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(seed)}&backgroundColor=0ea5e9,3b82f6,6366f1,8b5cf6,ec4899&textColor=ffffff`;
  };

  useEffect(() => {
    if (user?.email) {
      fetchTemplates();
      fetchEmailTemplates();
    }
  }, [user]);

  const fetchTemplates = async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`https://emailserverjuly.shahlimon.online/user-email-templates?ownerEmail=${user.email}`);
      setTemplates(res.data);

      // Extract unique categories
      const existingCategories = [
        ...new Set(res.data.map((u) => u.category).filter(Boolean)),
      ];
      setCategories((prev) => [...new Set([...prev, ...existingCategories])]);
    } catch (err) {
      toast.error("Error fetching data");
    }
  };

  const fetchEmailTemplates = async () => {
    if (!user?.email) return;
    try {
      const response = await fetch(`https://emailserverjuly.shahlimon.online/user-emails?ownerEmail=${user.email}`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setEmailTemplates(data);
    } catch (err) {
      toast.error(`Failed to fetch email templates: ${err.message}`);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        await axios.put(
          `https://emailserverjuly.shahlimon.online/edit-user-email-template/${editingId}`,
          { ...formData, ownerEmail: user?.email },
        );
        toast.success("User updated ✅");
      } else {
        await axios.post("https://emailserverjuly.shahlimon.online/add-user-email-template", {
          ...formData,
          enabled: true,
          ownerEmail: user?.email,
        });
        toast.success("User added ✅");
      }

      setFormData({
        userName: "",
        UserEmail: "",
        category: "forSubscriber",
        customFields: [],
      });
      setEditingId(null);
      setShowUserModal(false);
      setIsAddingNewCategory(false);
      setNewCategory("");
      fetchTemplates();
    } catch (err) {
      toast.error("Something went wrong ❌");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      userName: user.userName,
      UserEmail: user.UserEmail,
      category: user.category || "forSubscriber",
      customFields: user.customFields || [],
    });
    setEditingId(user._id);
    setShowUserModal(true);
  };

  const cancelEdit = () => {
    setFormData({
      userName: "",
      UserEmail: "",
      category: "forSubscriber",
      customFields: [],
    });
    setEditingId(null);
    setShowUserModal(false);
  };

  const uniqueCustomFields = React.useMemo(() => {
    const fields = new Set();
    templates.forEach(t => {
      if (t.customFields && Array.isArray(t.customFields)) {
        t.customFields.forEach(cf => {
          if (cf.label) fields.add(cf.label);
        });
      }
    });
    return Array.from(fields).sort();
  }, [templates]);

  const handleAddPredefinedField = (label) => {
    if (formData.customFields.some(cf => cf.label.toLowerCase() === label.toLowerCase())) {
      toast.error(`Field "${label}" is already added.`);
      return;
    }
    const shortcode = label
      .toLowerCase()
      .replace(/ /g, "_")
      .replace(/[^\w]/g, "");
    setFormData((prev) => ({
      ...prev,
      customFields: [
        ...prev.customFields,
        { label, value: "", shortcode },
      ],
    }));
  };

  const handleAddField = () => {
    setFormData((prev) => ({
      ...prev,
      customFields: [
        ...prev.customFields,
        { label: "", value: "", shortcode: "" },
      ],
    }));
  };

  const handleFieldChange = (index, field, value) => {
    const updatedFields = [...formData.customFields];
    updatedFields[index][field] = value;

    if (field === "label") {
      updatedFields[index].shortcode = value
        .toLowerCase()
        .replace(/ /g, "_")
        .replace(/[^\w]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      customFields: updatedFields,
    }));
  };

  const handleRemoveField = (index) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index),
    }));
  };

  const handleTemplateChange = (e) => {
    const selectedTemplate = emailTemplates.find(
      (template) => template._id === e.target.value,
    );
    if (selectedTemplate) {
      const user = templates.find((t) => t.UserEmail === messageForm.toEmail);
      const userName = user?.userName || "User";

      let subjectWithUserName = replaceShortcodesClient(
        selectedTemplate.subject,
        userName,
      );
      let bodyWithUserName = replaceShortcodesClient(
        selectedTemplate.body || "",
        userName,
      );

      setMessageForm({
        ...messageForm,
        subject: subjectWithUserName,
        messageBody: bodyWithUserName,
      });
    } else {
      setMessageForm({
        ...messageForm,
        subject: "",
        messageBody: "",
      });
    }
  };

  // Helper to replace shortcodes on the client side for preview
  const replaceShortcodesClient = (content, userName) => {
    if (!content) return "";
    let result = content;
    result = result.replace(/{{user_name}}/g, userName);
    result = result.replace(/{{first_name}}/g, userName.split(" ")[0]);
    result = result.replace(/{user\?\.userName}/g, userName);
    return result;
  };

  const handleSendMessage = async () => {
    setSendingMessage(true);
    try {
      const response = await axios.post(
        "https://emailserverjuly.shahlimon.online/send-message",
        { ...messageForm, ownerEmail: user?.email }
      );
      if (response.status === 200) {
        toast.success("Message sent successfully ✅");
        setShowMessageModal(false);
        setMessageForm({ toEmail: "", subject: "", messageBody: "" });
      } else {
        toast.error(response.data?.error || "Failed to send message.");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || "Error sending message.";
      toast.error(errorMsg);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBulkImport = async (data) => {
    setImportLoading(true);
    try {
      const response = await axios.post(
        "https://emailserverjuly.shahlimon.online/bulk-import-contacts",
        { contacts: data, ownerEmail: user?.email },
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setShowImportModal(false);
        fetchTemplates();
      } else {
        toast.error("Import failed");
      }
    } catch (err) {
      toast.error("Error during import");
      console.error(err);
    } finally {
      setImportLoading(false);
    }
  };

  const processImportData = (results) => {
    const data = results.data;
    const mappedContacts = data
      .filter((row) => {
        const keys = Object.keys(row);
        const emailKey = keys.find(
          (k) =>
            k.toLowerCase().trim() === "email" ||
            k.toLowerCase().trim() === "useremail",
        );
        return emailKey && row[emailKey];
      })
      .map((row) => {
        const keys = Object.keys(row);
        const getValue = (targetKey) => {
          const key = keys.find(
            (k) => k.toLowerCase().trim() === targetKey.toLowerCase(),
          );
          return key ? row[key] : null;
        };

        const email = getValue("email") || getValue("useremail");
        const name =
          getValue("name") ||
          getValue("username") ||
          (email ? email.split("@")[0] : "User");
        const category = getValue("category") || "forSubscriber";

        const standardKeys = [
          "email",
          "useremail",
          "name",
          "username",
          "category",
        ];
        const customFields = keys
          .filter((key) => !standardKeys.includes(key.toLowerCase().trim()))
          .map((key) => ({
            label: key,
            value: row[key],
            shortcode: key
              .toLowerCase()
              .trim()
              .replace(/ /g, "_")
              .replace(/[^\w]/g, ""),
          }));

        return {
          userName: name,
          UserEmail: email,
          category: category,
          customFields: customFields,
          enabled: true,
        };
      });

    if (mappedContacts.length === 0) {
      toast.error("No valid contacts found");
      setImportLoading(false);
      return;
    }

    handleBulkImport(mappedContacts);
  };

  const downloadSampleCSV = () => {
    const headers = ["UserEmail", "userName", "category", "Phone", "Location"];
    const rows = [
      [
        "john@example.com",
        "John Doe",
        "forSubscriber",
        "+123456789",
        "New York",
      ],
      ["jane@example.com", "Jane Smith", "forRegister", "+987654321", "London"],
    ];

    let csvContent = headers.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "contacts_sample.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: processImportData,
        error: (err) => {
          toast.error("Error parsing CSV");
          console.error(err);
        },
      });
    }
  };

  const handleGoogleSheetsImport = async () => {
    if (!googleSheetLink) {
      toast.error("Please enter a Google Sheets link");
      return;
    }

    const match = googleSheetLink.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (!match) {
      toast.error("Invalid Google Sheets link");
      return;
    }

    const sheetId = match[1];
    const exportUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;

    setImportLoading(true);
    try {
      const response = await axios.get(exportUrl);
      Papa.parse(response.data, {
        header: true,
        skipEmptyLines: true,
        complete: processImportData,
        error: (err) => {
          toast.error("Error parsing data");
          setImportLoading(false);
        },
      });
    } catch (err) {
      toast.error("Failed to fetch sheet. Ensure it is public.");
      setImportLoading(false);
    }
  };

  const totalPages = Math.ceil(templates.length / itemsPerPage);

  const getPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
      }
    }
    return pages;
  };

  const formatDate = (dateStr) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateStr).toLocaleString("en-US", options);
  };

  const filteredTemplates = templates.filter((t) =>
    `${t.userName} ${t.UserEmail}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const paginatedTemplates = filteredTemplates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedUserLogs, setSelectedUserLogs] = useState([]);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  const [logPage, setLogPage] = useState(1);
  const logsPerPage = 5;
  const totalLogPages = Math.ceil(selectedUserLogs.length / logsPerPage);

  const paginatedLogs = selectedUserLogs.slice(
    (logPage - 1) * logsPerPage,
    logPage * logsPerPage,
  );

  const handleLogPageChange = (direction) => {
    if (direction === "prev" && logPage > 1) setLogPage(logPage - 1);
    if (direction === "next" && logPage < totalLogPages)
      setLogPage(logPage + 1);
  };

  const handleToggleUser = async (user) => {
    try {
      const updatedUser = {
        ...user,
        enabled: !user.enabled,
      };

      await axios.put(
        `https://emailserverjuly.shahlimon.online/edit-user-email-template/${user._id}`,
        updatedUser,
      );

      toast.success(
        `User ${updatedUser.enabled ? "enabled" : "disabled"} successfully`,
      );
      fetchTemplates(); // Refresh list
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`https://emailserverjuly.shahlimon.online/user-email-template/${id}?ownerEmail=${user?.email}`);
        toast.success("Deleted successfully 🗑️");
        fetchTemplates();
      } catch (err) {
        toast.error("Failed to delete ❌");
      }
    }
  };

  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(paginatedTemplates.map((user) => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (id) => {
    if (selectedUsers.includes(id)) {
      setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedUsers.length} selected users?`)) {
      try {
        await axios.post('https://emailserverjuly.shahlimon.online/bulk-delete-user-email-templates', { ids: selectedUsers, ownerEmail: user?.email });
        toast.success('Selected users deleted successfully 🗑️');
        setSelectedUsers([]);
        fetchTemplates();
      } catch (err) {
        toast.error('Failed to delete selected users ❌');
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container-fluid py-5 position-relative"
      id="premium-dark-manager"
      style={{
        width: "100%",
        minHeight: "100vh",
        background: "#0f172a",
        color: "#f8fafc",
        overflow: "hidden",
      }}
    >
      {/* Background Orbs */}
      <div className="position-absolute top-0 start-0 w-100 h-100 overflow-hidden" style={{ zIndex: 0, pointerEvents: "none" }}>
        <div style={{ position: "absolute", top: "-10%", left: "-5%", width: "40vw", height: "40vw", background: "radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(0,0,0,0) 70%)", borderRadius: "50%", filter: "blur(60px)" }} />
        <div style={{ position: "absolute", bottom: "-10%", right: "-5%", width: "40vw", height: "40vw", background: "radial-gradient(circle, rgba(14,165,233,0.15) 0%, rgba(0,0,0,0) 70%)", borderRadius: "50%", filter: "blur(60px)" }} />
      </div>

      <div className="position-relative" style={{ zIndex: 1 }}>
        {/* Navigation & Header */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div>
            <Link
              to="/"
              className="btn btn-link text-decoration-none px-0 mb-3 d-inline-flex align-items-center"
              style={{ color: "#94a3b8", fontSize: "0.9rem", transition: "color 0.2s" }}
              onMouseOver={(e) => (e.currentTarget.style.color = "#f8fafc")}
              onMouseOut={(e) => (e.currentTarget.style.color = "#94a3b8")}
            >
              <ArrowLeft size={16} className="me-2" />
              Back to Dashboard
            </Link>
            <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}>
              <h2 className="fw-bold mb-1" style={{ background: "linear-gradient(to right, #fff, #94a3b8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Audience CRM
              </h2>
              <p style={{ color: "#64748b", fontSize: "0.95rem" }}>
                Manage your subscribers and leads with AI-powered insights.
              </p>
            </motion.div>
          </div>

          <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="d-flex align-items-center gap-3">
            {selectedUsers.length > 0 && (
              <button
                className="btn d-flex align-items-center"
                style={{ background: "rgba(239, 68, 68, 0.15)", color: "#f87171", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "12px", padding: "0.5rem 1rem", backdropFilter: "blur(10px)" }}
                onClick={handleBulkDelete}
              >
                <Trash2 size={18} className="me-2" />
                Delete ({selectedUsers.length})
              </button>
            )}
            <button
              className="btn d-flex align-items-center shadow-sm"
              style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "white", border: "none", borderRadius: "12px", padding: "0.5rem 1.25rem", fontWeight: "500" }}
              onClick={() => {
                setEditingId(null);
                setFormData({ userName: "", UserEmail: "", category: "forSubscriber", customFields: [] });
                setShowUserModal(true);
              }}
            >
              <UserPlus size={18} className="me-2" />
              Add User
            </button>
            <button
              className="btn d-flex align-items-center"
              style={{ background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "0.5rem 1.25rem", backdropFilter: "blur(10px)" }}
              onClick={() => setShowImportModal(true)}
            >
              <Upload size={18} className="me-2 text-info" />
              Import
            </button>
          </motion.div>
        </div>

        {/* Toolbar: Search and Filters */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="d-flex justify-content-between align-items-center mb-4 p-3 rounded-4"
          style={{ background: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(255,255,255,0.05)", backdropFilter: "blur(12px)" }}
        >
          <div className="d-flex align-items-center text-muted" style={{ fontSize: "0.9rem" }}>
            <span className="fw-semibold text-white me-2">{filteredTemplates.length}</span> Total Contacts
          </div>
          <div className="input-group" style={{ width: "350px" }}>
            <span className="input-group-text border-0" style={{ background: "rgba(0,0,0,0.2)", color: "#64748b" }}>
              <Search size={16} />
            </span>
            <input
              type="text"
              className="form-control border-0 shadow-none text-white placeholder-muted"
              placeholder="Search audiences..."
              style={{ background: "rgba(0,0,0,0.2)", fontSize: "0.95rem" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </motion.div>

        {/* Glassmorphic Data Table */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="card shadow-lg border-0 rounded-4 overflow-hidden"
          style={{ background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(16px)", border: "1px solid rgba(255, 255, 255, 0.05)" }}
        >
          <div className="table-responsive">
            <table className="table table-borderless table-hover mb-0 align-middle">
              <thead style={{ background: "rgba(255,255,255,0.02)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <tr>
                  <th className="px-4 py-3" style={{ width: "50px" }}>
                    <div className="form-check custom-checkbox">
                      <input
                        type="checkbox"
                        className="form-check-input bg-dark border-secondary"
                        checked={paginatedTemplates.length > 0 && selectedUsers.length === paginatedTemplates.length}
                        onChange={handleSelectAll}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </th>
                  <th className="py-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", letterSpacing: "1px", fontWeight: "600" }}>Contact</th>
                  <th className="py-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", letterSpacing: "1px", fontWeight: "600" }}>Category</th>
                  <th className="py-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", letterSpacing: "1px", fontWeight: "600" }}>Engagement</th>
                  <th className="py-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", letterSpacing: "1px", fontWeight: "600" }}>Status</th>
                  <th className="py-3 text-end px-4 text-uppercase text-muted" style={{ fontSize: "0.75rem", letterSpacing: "1px", fontWeight: "600" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {paginatedTemplates.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <td colSpan="6" className="text-center py-5">
                        <div className="d-flex flex-column align-items-center text-muted">
                          <User size={48} className="mb-3 opacity-25" />
                          <h6 className="fw-semibold text-white">No contacts found</h6>
                          <p className="small mb-0">Try adjusting your search or add a new user.</p>
                        </div>
                      </td>
                    </motion.tr>
                  ) : (
                    paginatedTemplates.map((user, index) => (
                      <motion.tr
                        key={user._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ delay: index * 0.05 }}
                        style={{ borderBottom: "1px solid rgba(255,255,255,0.03)", transition: "background 0.2s" }}
                        className="log-row-hover"
                      >
                        <td className="px-4">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input bg-dark border-secondary"
                              checked={selectedUsers.includes(user._id)}
                              onChange={() => handleSelectUser(user._id)}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <img src={getAvatarUrl(user.userName, user.UserEmail)} alt="avatar" className="rounded-circle me-3 border border-dark" style={{ width: "42px", height: "42px" }} />
                            <div>
                              <div className="fw-semibold text-white" style={{ fontSize: "0.95rem" }}>{user.userName || "Unnamed User"}</div>
                              <div className="text-muted" style={{ fontSize: "0.85rem" }}>{user.UserEmail}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className="badge rounded-pill px-3 py-2 fw-medium"
                            style={{
                              background: user.category === "forSubscriber" ? "rgba(14,165,233,0.15)" : user.category === "forRegister" ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.15)",
                              color: user.category === "forSubscriber" ? "#38bdf8" : user.category === "forRegister" ? "#34d399" : "#cbd5e1",
                              border: `1px solid ${user.category === "forSubscriber" ? "rgba(14,165,233,0.3)" : user.category === "forRegister" ? "rgba(16,185,129,0.3)" : "rgba(148,163,184,0.3)"}`
                            }}
                          >
                            {user.category === "forSubscriber" ? "Subscriber" : user.category === "forRegister" ? "Register" : user.category || "Uncategorized"}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-icon rounded-circle d-flex align-items-center justify-content-center"
                              title="Send Message"
                              style={{ width: "36px", height: "36px", background: "rgba(59, 130, 246, 0.15)", color: "#60a5fa", border: "1px solid rgba(59, 130, 246, 0.3)", transition: "all 0.2s" }}
                              onClick={() => {
                                setMessageForm({ toEmail: user.UserEmail, subject: "", messageBody: "" });
                                setShowMessageModal(true);
                              }}
                              onMouseOver={(e) => { e.currentTarget.style.background = "#3b82f6"; e.currentTarget.style.color = "#fff"; }}
                              onMouseOut={(e) => { e.currentTarget.style.background = "rgba(59, 130, 246, 0.15)"; e.currentTarget.style.color = "#60a5fa"; }}
                            >
                              <Send size={16} />
                            </button>
                            <button
                              className="btn btn-sm btn-icon rounded-circle d-flex align-items-center justify-content-center"
                              title="Message History"
                              style={{ width: "36px", height: "36px", background: "rgba(139, 92, 246, 0.15)", color: "#a78bfa", border: "1px solid rgba(139, 92, 246, 0.3)", transition: "all 0.2s" }}
                              onClick={() => {
                                setSelectedUserLogs(user.messageLogs || []);
                                setLogPage(1);
                                setShowLogModal(true);
                              }}
                              onMouseOver={(e) => { e.currentTarget.style.background = "#8b5cf6"; e.currentTarget.style.color = "#fff"; }}
                              onMouseOut={(e) => { e.currentTarget.style.background = "rgba(139, 92, 246, 0.15)"; e.currentTarget.style.color = "#a78bfa"; }}
                            >
                              <History size={16} />
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="form-check form-switch custom-switch-premium m-0 p-0 d-flex align-items-center">
                            <input
                              className="form-check-input ms-0 me-2"
                              type="checkbox"
                              checked={user.enabled ?? true}
                              onChange={() => handleToggleUser(user)}
                              style={{ width: "2.5em", height: "1.25em", cursor: "pointer" }}
                            />
                            <span className="small text-muted" style={{ minWidth: "50px" }}>{user.enabled !== false ? "Active" : "Paused"}</span>
                          </div>
                        </td>
                        <td className="text-end px-4">
                          <button
                            className="btn btn-sm text-muted me-2 p-1 btn-action-hover"
                            onClick={() => setSelectedViewUser(user)}
                            title="View Contact"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="btn btn-sm text-muted me-2 p-1 btn-action-hover"
                            onClick={() => handleEdit(user)}
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            className="btn btn-sm text-muted p-1 btn-action-hover text-danger-hover"
                            onClick={() => handleDelete(user._id)}
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-3 border-top" style={{ borderColor: "rgba(255,255,255,0.05) !important", background: "rgba(255,255,255,0.01)" }}>
              <ul className="pagination justify-content-end mb-0 pagination-premium">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link rounded-start-pill px-3" onClick={() => setCurrentPage(currentPage - 1)}>Prev</button>
                </li>
                {getPagination().map((page, i) =>
                  page === "..." ? (
                    <li key={i} className="page-item disabled"><span className="page-link">...</span></li>
                  ) : (
                    <li key={i} className={`page-item ${page === currentPage ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                    </li>
                  )
                )}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link rounded-end-pill px-3" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                </li>
              </ul>
            </div>
          )}
        </motion.div>
      </div>

      {/* Message Modal */}
      <AnimatePresence>
        {showMessageModal && (
          <div
            className="modal-backdrop-custom fixed-top w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ background: "rgba(15, 23, 42, 0.75)", backdropFilter: "blur(12px)", zIndex: 1050, position: "fixed", top: 0, left: 0 }}
            onClick={() => setShowMessageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="modal-dialog m-0 w-100"
              style={{ maxWidth: "650px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg overflow-hidden" style={{ background: "rgba(30, 41, 59, 0.85)", backdropFilter: "blur(20px)", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="modal-header border-0 px-4 pt-4 pb-3">
                  <h5 className="modal-title fw-bold text-white d-flex align-items-center">
                    <Send size={20} className="me-2 text-primary" />
                    Send Message
                  </h5>
                  <button type="button" className="btn-close btn-close-white opacity-50" onClick={() => setShowMessageModal(false)}></button>
                </div>
                <div className="modal-body px-4 pb-4">
                  <div className="mb-4">
                    <label className="form-label text-muted small text-uppercase fw-semibold mb-2">Email Template</label>
                    <select
                      className="form-select form-select-lg shadow-none"
                      style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc", fontSize: "0.95rem" }}
                      onChange={handleTemplateChange}
                      defaultValue=""
                    >
                      <option value="" className="text-dark">Select a template</option>
                      {emailTemplates
                        .filter((template) => {
                          const recipientUser = templates.find((t) => t.UserEmail === messageForm.toEmail);
                          if (!recipientUser || !recipientUser.category) return true;
                          return template.category === recipientUser.category;
                        })
                        .map((template) => (
                          <option key={template._id} value={template._id} className="text-dark">
                            {template.templateName}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="form-label text-muted small text-uppercase fw-semibold mb-2">To Email</label>
                    <input
                      type="email"
                      className="form-control shadow-none"
                      style={{ background: "rgba(15, 23, 42, 0.3)", border: "1px solid rgba(255,255,255,0.05)", color: "#94a3b8" }}
                      value={messageForm.toEmail}
                      readOnly
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label text-muted small text-uppercase fw-semibold mb-2">Subject</label>
                    <input
                      type="text"
                      className="form-control shadow-none"
                      style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc" }}
                      value={messageForm.subject}
                      onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label text-muted small text-uppercase fw-semibold mb-2">Message Body <span className="text-secondary text-lowercase fw-normal">(HTML allowed)</span></label>
                    <textarea
                      className="form-control shadow-none"
                      rows="6"
                      placeholder="<h2>Hello</h2><p>This is <strong>bold</strong> text</p>"
                      style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc" }}
                      value={messageForm.messageBody}
                      onChange={(e) => setMessageForm({ ...messageForm, messageBody: e.target.value })}
                    />
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4 pt-0">
                  <button className="btn btn-link text-muted text-decoration-none px-4" onClick={() => setShowMessageModal(false)}>Cancel</button>
                  <button className="btn btn-primary rounded-pill px-4" onClick={handleSendMessage} disabled={sendingMessage} style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", border: "none" }}>
                    {sendingMessage ? <><Loader2 size={16} className="animate-spin me-2 d-inline" />Sending...</> : "Send Message"}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Log Modal */}
      <AnimatePresence>
        {showLogModal && (
          <div
            className="modal-backdrop-custom fixed-top w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(12px)", zIndex: 1050, position: "fixed", top: 0, left: 0 }}
            onClick={() => setShowLogModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="modal-dialog m-0 w-100"
              style={{ maxWidth: "800px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg" style={{ background: "rgba(30, 41, 59, 0.95)", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden" }}>
                <div className="modal-header border-0 px-4 pt-4 pb-0">
                  <h5 className="modal-title fw-bold text-white d-flex align-items-center">
                    <History size={20} className="me-2 text-info" />
                    Message History
                  </h5>
                  <button type="button" className="btn-close btn-close-white opacity-50" onClick={() => setShowLogModal(false)}></button>
                </div>
                <div className="modal-body p-4">
                  {selectedUserLogs.length === 0 ? (
                    <div className="text-center py-5">
                      <MailOpen size={48} className="text-muted mb-3 opacity-50" />
                      <p className="text-muted fs-5 mb-0">No message logs found.</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive rounded-4 border border-secondary border-opacity-25 mb-4">
                        <table className="table table-borderless table-hover mb-0 align-middle text-white">
                          <thead style={{ background: "rgba(15, 23, 42, 0.6)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                            <tr>
                              <th className="py-3 px-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", fontWeight: "600" }}>#</th>
                              <th className="py-3 px-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", fontWeight: "600" }}>Day</th>
                              <th className="py-3 px-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", fontWeight: "600" }}>Sent Date</th>
                              <th className="py-3 px-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", fontWeight: "600" }}>Template</th>
                              <th className="py-3 px-3 text-uppercase text-muted" style={{ fontSize: "0.75rem", fontWeight: "600" }}>Type</th>
                              <th className="py-3 px-3 text-end text-uppercase text-muted" style={{ fontSize: "0.75rem", fontWeight: "600" }}>View</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedLogs.map((log, idx) => (
                              <tr
                                key={idx}
                                style={{ cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.02)" }}
                                onClick={() => setSelectedLogDetail(log)}
                                className="log-row-hover"
                              >
                                <td className="px-3">{(logPage - 1) * logsPerPage + idx + 1}</td>
                                <td className="px-3">
                                  <span className="badge bg-primary bg-opacity-25 text-primary rounded-pill px-2">Day {log.day}</span>
                                </td>
                                <td className="px-3 text-muted small">{formatDate(log.date)}</td>
                                <td className="px-3 fw-medium">{log.templateName}</td>
                                <td className="px-3">
                                  <span className={`badge rounded-pill px-2 py-1 ${log.sendType === "manual" ? "bg-warning bg-opacity-25 text-warning" : "bg-info bg-opacity-25 text-info"}`}>
                                    {log.sendType === "manual" ? "Manual" : log.sendType === "scheduled" ? "Scheduled" : "Auto"}
                                  </span>
                                </td>
                                <td className="px-3 text-end">
                                  <button
                                    className="btn btn-sm btn-link text-info p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedLogDetail(log);
                                    }}
                                  >
                                    <Eye size={18} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalLogPages > 1 && (
                        <nav className="d-flex justify-content-center">
                          <ul className="pagination pagination-sm pagination-premium mb-0">
                            <li className={`page-item ${logPage === 1 ? "disabled" : ""}`}>
                              <button className="page-link rounded-start-pill" onClick={() => handleLogPageChange("prev")}>Prev</button>
                            </li>
                            {[...Array(totalLogPages)].map((_, i) => (
                              <li key={i} className={`page-item ${logPage === i + 1 ? "active" : ""}`}>
                                <button className="page-link" onClick={() => setLogPage(i + 1)}>{i + 1}</button>
                              </li>
                            ))}
                            <li className={`page-item ${logPage === totalLogPages ? "disabled" : ""}`}>
                              <button className="page-link rounded-end-pill" onClick={() => handleLogPageChange("next")}>Next</button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View Contact Details Modal */}
      <AnimatePresence>
        {selectedViewUser && (
          <div
            className="modal-backdrop-custom fixed-top w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(12px)", zIndex: 1060, position: "fixed", top: 0, left: 0 }}
            onClick={() => setSelectedViewUser(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="modal-dialog m-0 w-100 modal-dialog-scrollable"
              style={{ maxWidth: "600px", maxHeight: "90vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg" style={{ background: "rgba(30, 41, 59, 0.95)", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden" }}>
                {/* Header */}
                <div className="modal-header border-0 px-4 pt-4 pb-3" style={{ background: "rgba(15, 23, 42, 0.5)" }}>
                  <div>
                    <h5 className="modal-title fw-bold text-white d-flex align-items-center mb-1">
                      <User size={20} className="me-2 text-info" />
                      Contact Details
                    </h5>
                    <p className="mb-0 text-muted small">
                      Detailed view of this subscriber's profile.
                    </p>
                  </div>
                  <button type="button" className="btn-close btn-close-white opacity-50" onClick={() => setSelectedViewUser(null)}></button>
                </div>

                <div className="modal-body p-4" style={{ customScrollbar: "true", overflowY: "auto", maxHeight: "60vh" }}>
                  <div className="d-flex align-items-center mb-4">
                    <img src={getAvatarUrl(selectedViewUser.userName, selectedViewUser.UserEmail)} alt="avatar" className="rounded-circle me-3 border border-dark" style={{ width: "64px", height: "64px" }} />
                    <div>
                      <div className="fw-bold text-white" style={{ fontSize: "1.2rem" }}>{selectedViewUser.userName || "Unnamed User"}</div>
                      <div className="text-info">{selectedViewUser.UserEmail}</div>
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-6">
                      <div className="p-3 rounded-4 h-100" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.5px" }}>Category</div>
                        <span className="badge rounded-pill px-3 py-2 fw-medium" style={{ background: "rgba(14,165,233,0.15)", color: "#38bdf8", border: "1px solid rgba(14,165,233,0.3)" }}>
                          {selectedViewUser.category === "forSubscriber" ? "Subscriber" : selectedViewUser.category === "forRegister" ? "Register" : selectedViewUser.category || "Uncategorized"}
                        </span>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-3 rounded-4 h-100" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.5px" }}>Status</div>
                        <div className="d-flex align-items-center mt-1">
                          <div className={`rounded-circle me-2 ${selectedViewUser.enabled !== false ? 'bg-success' : 'bg-secondary'}`} style={{ width: '8px', height: '8px' }}></div>
                          <span className="text-white">{selectedViewUser.enabled !== false ? "Active" : "Paused"}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 rounded-4" style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.05)" }}>
                    <div className="text-muted small text-uppercase fw-semibold mb-3 d-flex align-items-center">
                      <Tag size={14} className="me-2" /> Custom Fields
                    </div>
                    {(!selectedViewUser.customFields || selectedViewUser.customFields.length === 0) ? (
                      <div className="text-muted small">No custom fields associated with this contact.</div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {selectedViewUser.customFields.map((field, idx) => (
                          <div key={idx} className="d-flex justify-content-between p-2 rounded" style={{ background: "rgba(255,255,255,0.03)" }}>
                            <span className="text-muted">{field.label}:</span>
                            <span className="text-white fw-medium">{field.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer border-0 px-4 py-3" style={{ background: "rgba(15, 23, 42, 0.5)" }}>
                  <button className="btn btn-primary rounded-pill px-4 border-0" style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" }} onClick={() => setSelectedViewUser(null)}>Done</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Email Detail Modal */}
      <AnimatePresence>
        {selectedLogDetail && (
          <div
            className="modal-backdrop-custom fixed-top w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(12px)", zIndex: 1060, position: "fixed", top: 0, left: 0 }}
            onClick={() => setSelectedLogDetail(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="modal-dialog m-0 w-100 modal-dialog-scrollable"
              style={{ maxWidth: "1200px", maxHeight: "90vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg" style={{ background: "rgba(30, 41, 59, 0.95)", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
                {/* Header */}
                <div className="modal-header border-0 px-4 pt-4 pb-3" style={{ background: "rgba(15, 23, 42, 0.5)" }}>
                  <div>
                    <h5 className="modal-title fw-bold text-white d-flex align-items-center mb-1">
                      <MailOpen size={20} className="me-2 text-primary" />
                      Email Details
                    </h5>
                    <p className="mb-0 text-muted small">
                      {selectedLogDetail.templateName} • {formatDate(selectedLogDetail.date)}
                    </p>
                  </div>
                  <button type="button" className="btn-close btn-close-white opacity-50" onClick={() => setSelectedLogDetail(null)}></button>
                </div>

                <div className="modal-body p-0 overflow-auto" style={{ customScrollbar: "true" }}>
                  {/* Info Cards Row */}
                  <div className="p-4" style={{ background: "rgba(15, 23, 42, 0.3)" }}>
                    <div className="row g-3">
                      {/* Recipient Card */}
                      <div className="col-md-4">
                        <div className="d-flex align-items-center p-3 rounded-4 h-100" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: "44px", height: "44px", background: "rgba(59, 130, 246, 0.15)", color: "#60a5fa", flexShrink: 0 }}>
                            <Mail size={20} />
                          </div>
                          <div style={{ overflow: "hidden" }}>
                            <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.5px" }}>Recipient</div>
                            <div className="fw-semibold text-white text-truncate" style={{ fontSize: "0.95rem" }}>{selectedLogDetail.recipientName || "—"}</div>
                            <div className="text-muted text-truncate small">{selectedLogDetail.recipientEmail || "—"}</div>
                          </div>
                        </div>
                      </div>

                      {/* Send Info Card */}
                      <div className="col-md-4">
                        <div className="d-flex align-items-center p-3 rounded-4 h-100" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: "44px", height: "44px", background: "rgba(236, 72, 153, 0.15)", color: "#f472b6", flexShrink: 0 }}>
                            <Send size={20} />
                          </div>
                          <div>
                            <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.5px" }}>Send Info</div>
                            <div className="fw-semibold text-white mb-1" style={{ fontSize: "0.95rem" }}>Day {selectedLogDetail.day}</div>
                            <div>
                              <span className={`badge rounded-pill me-1 ${selectedLogDetail.sendType === "manual" ? "bg-warning bg-opacity-25 text-warning" : "bg-info bg-opacity-25 text-info"}`} style={{ fontSize: "0.7rem" }}>
                                {selectedLogDetail.sendType === "manual" ? "✋ Manual" : "⏰ Scheduled"}
                              </span>
                              <span className={`badge rounded-pill ${selectedLogDetail.mode === "ai" ? "bg-purple bg-opacity-25 text-purple" : "bg-secondary bg-opacity-25 text-secondary"}`} style={{ fontSize: "0.7rem", color: selectedLogDetail.mode === "ai" ? "#c084fc" : "#94a3b8" }}>
                                {selectedLogDetail.mode === "ai" ? "🤖 AI Generated" : "📝 HTML"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Category & Date Card */}
                      <div className="col-md-4">
                        <div className="d-flex align-items-center p-3 rounded-4 h-100" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: "44px", height: "44px", background: "rgba(16, 185, 129, 0.15)", color: "#34d399", flexShrink: 0 }}>
                            <Clock size={20} />
                          </div>
                          <div>
                            <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.5px" }}>Sent At</div>
                            <div className="fw-semibold text-white mb-1" style={{ fontSize: "0.95rem" }}>{formatDate(selectedLogDetail.date)}</div>
                            {selectedLogDetail.category && (
                              <span className={`badge rounded-pill ${selectedLogDetail.category === "forSubscriber" ? "bg-info bg-opacity-25 text-info" : selectedLogDetail.category === "forRegister" ? "bg-success bg-opacity-25 text-success" : "bg-secondary bg-opacity-25 text-secondary"}`} style={{ fontSize: "0.7rem" }}>
                                {selectedLogDetail.category === "forSubscriber" ? "Subscriber" : selectedLogDetail.category === "forRegister" ? "Register" : selectedLogDetail.category}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subject Line */}
                  <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.5px" }}>Subject</div>
                    <div className="fw-bold text-white" style={{ fontSize: "1.1rem" }}>{selectedLogDetail.subject || "(No subject saved)"}</div>
                  </div>

                  {/* Email Body */}
                  <div className="p-4">
                    <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: "0.5px" }}>Email Body</div>
                    {selectedLogDetail.body ? (
                      <div
                        className="email-preview-container custom-scrollbar"
                        style={{
                          background: "#ffffff",
                          color: "#1e293b",
                          borderRadius: "12px",
                          padding: "24px",
                          minHeight: "200px",
                          maxHeight: "400px",
                          overflowY: "auto",
                          overflowX: "hidden",
                          boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)",
                          fontFamily: "Inter, sans-serif"
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: `<style>
                            .email-preview-container { word-wrap: break-word !important; overflow-wrap: break-word !important; word-break: break-word !important; }
                            .email-preview-container * { max-width: 100% !important; box-sizing: border-box !important; }
                            .email-preview-container img { height: auto !important; }
                          </style>` + selectedLogDetail.body 
                        }}
                      />
                    ) : (
                      <div className="text-center py-5 rounded-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                        <MailOpen size={40} className="text-muted mb-2 opacity-50" />
                        <p className="text-muted mb-0">Email body not available for this log entry.<br /><small>Older logs may not have saved the full email content.</small></p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer border-0 px-4 py-3" style={{ background: "rgba(15, 23, 42, 0.5)" }}>
                  <button className="btn btn-outline-light rounded-pill px-4" style={{ borderColor: "rgba(255,255,255,0.1)", color: "#94a3b8" }} onClick={() => setSelectedLogDetail(null)}>Close</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add/Edit User Modal */}
      <AnimatePresence>
        {showUserModal && (
          <div
            className="modal-backdrop-custom fixed-top w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ background: "rgba(15, 23, 42, 0.75)", backdropFilter: "blur(12px)", zIndex: 1050, position: "fixed", top: 0, left: 0 }}
            onClick={cancelEdit}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="modal-dialog m-0 w-100"
              style={{ maxWidth: "700px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg overflow-hidden" style={{ background: "rgba(30, 41, 59, 0.95)", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="modal-header border-0 px-4 pt-4 pb-3">
                  <h5 className="modal-title fw-bold text-white d-flex align-items-center">
                    {editingId ? <><Edit size={20} className="me-2 text-warning" />Edit Contact</> : <><UserPlus size={20} className="me-2 text-primary" />Add New Contact</>}
                  </h5>
                  <button type="button" className="btn-close btn-close-white opacity-50" onClick={cancelEdit}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body px-4 pb-4 custom-scrollbar overflow-auto" style={{ maxHeight: "75vh" }}>
                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label text-muted small text-uppercase fw-semibold mb-2">User Name</label>
                        <input
                          type="text"
                          name="userName"
                          value={formData.userName}
                          onChange={handleChange}
                          className="form-control shadow-none"
                          style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc" }}
                          placeholder="e.g. John Doe"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label text-muted small text-uppercase fw-semibold mb-2">User Email</label>
                        <input
                          type="email"
                          name="UserEmail"
                          value={formData.UserEmail}
                          onChange={handleChange}
                          className="form-control shadow-none"
                          style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc" }}
                          placeholder="e.g. john@example.com"
                          required
                        />
                      </div>
                      <div className="col-md-12">
                        <label className="form-label text-muted small text-uppercase fw-semibold mb-2">Email Category</label>
                        <div className="d-flex gap-2">
                          <select
                            name="category"
                            value={isAddingNewCategory ? "addNew" : formData.category}
                            onChange={(e) => {
                              if (e.target.value === "addNew") {
                                setIsAddingNewCategory(true);
                              } else {
                                setIsAddingNewCategory(false);
                                handleChange(e);
                              }
                            }}
                            className="form-select shadow-none"
                            style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc" }}
                            required
                          >
                            {categories.map((cat) => (
                              <option key={cat} value={cat} className="text-dark">
                                {cat === "forSubscriber" ? "Subscriber" : cat === "forRegister" ? "Register" : cat}
                              </option>
                            ))}
                            <option value="addNew" className="text-primary fw-bold">+ Add New Category...</option>
                          </select>
                          {isAddingNewCategory && (
                            <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: "auto", opacity: 1 }} className="input-group">
                              <input
                                type="text"
                                className="form-control shadow-none"
                                style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc" }}
                                placeholder="New category name"
                                value={newCategory}
                                onChange={(e) => {
                                  setNewCategory(e.target.value);
                                  setFormData((prev) => ({ ...prev, category: e.target.value }));
                                }}
                                required
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                style={{ borderColor: "rgba(255,255,255,0.1)", color: "#94a3b8" }}
                                onClick={() => {
                                  setIsAddingNewCategory(false);
                                  setFormData((prev) => ({ ...prev, category: "forSubscriber" }));
                                }}
                              >
                                <X size={14} />
                              </button>
                            </motion.div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-12 mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="form-label text-muted small text-uppercase fw-semibold mb-0">Custom Fields</label>
                          <button type="button" className="btn btn-sm rounded-pill d-flex align-items-center" style={{ background: "rgba(59, 130, 246, 0.15)", color: "#60a5fa", border: "1px solid rgba(59, 130, 246, 0.3)" }} onClick={handleAddField}>
                            <Plus size={14} className="me-1" /> Add Field
                          </button>
                        </div>

                        {uniqueCustomFields.length > 0 && (
                          <div className="mb-3 p-3 rounded-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                            <div className="text-muted small mb-2 d-flex align-items-center">
                              <Tag size={12} className="me-1" /> Click a previous field to add it:
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              {uniqueCustomFields.map((fieldLabel) => (
                                <button
                                  key={fieldLabel}
                                  type="button"
                                  className="btn btn-sm rounded-pill d-flex align-items-center transition-all"
                                  style={{ background: "rgba(255,255,255,0.05)", color: "#cbd5e1", border: "1px solid rgba(255,255,255,0.1)", fontSize: "0.75rem" }}
                                  onClick={() => handleAddPredefinedField(fieldLabel)}
                                >
                                  <Plus size={12} className="me-1" /> {fieldLabel}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <AnimatePresence>
                          {formData.customFields.map((field, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="row g-2 mb-3 align-items-end p-3 rounded-4"
                              style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}
                            >
                              <div className="col-12 col-sm-6 col-md-4">
                                <label className="form-label text-muted small mb-1">Label</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm shadow-none"
                                  style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc" }}
                                  placeholder="e.g. Company"
                                  value={field.label}
                                  onChange={(e) => handleFieldChange(index, "label", e.target.value)}
                                  required
                                />
                              </div>
                              <div className="col-12 col-sm-6 col-md-4">
                                <label className="form-label text-muted small mb-1">Value</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm shadow-none"
                                  style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(255,255,255,0.1)", color: "#f8fafc" }}
                                  placeholder="e.g. Acme Corp"
                                  value={field.value}
                                  onChange={(e) => handleFieldChange(index, "value", e.target.value)}
                                  required
                                />
                              </div>
                              <div className="col-10 col-md-3">
                                <label className="form-label text-muted small mb-1">Shortcode</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm shadow-none font-monospace text-info"
                                  style={{ background: "rgba(0, 0, 0, 0.2)", border: "none" }}
                                  value={`{{${field.shortcode}}}`}
                                  readOnly
                                />
                              </div>
                              <div className="col-2 col-md-1 d-flex justify-content-end pb-1">
                                <button type="button" className="btn btn-sm btn-icon rounded-circle" style={{ background: "rgba(239, 68, 68, 0.15)", color: "#f87171", border: "none", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleRemoveField(index)}>
                                  <Trash2 size={14} style={{ margin: "auto" }} />
                                </button>
                              </div>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                        {formData.customFields.length === 0 && (
                          <div className="text-center py-4 rounded-4" style={{ background: "rgba(255,255,255,0.01)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                            <p className="text-muted small mb-0">No custom fields added yet.<br />Use fields to personalize your emails.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-0 px-4 pb-4 pt-0">
                    <button type="button" className="btn btn-link text-muted text-decoration-none px-4" onClick={cancelEdit}>Cancel</button>
                    <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={loading} style={{ background: editingId ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", border: "none" }}>
                      {loading ? <><Loader2 size={16} className="animate-spin me-2 d-inline" />Saving...</> : editingId ? "Update Contact" : "Add Contact"}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Import Contacts Modal */}
      <AnimatePresence>
        {showImportModal && (
          <div
            className="modal-backdrop-custom fixed-top w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(12px)", zIndex: 1050, position: "fixed", top: 0, left: 0 }}
            onClick={() => setShowImportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="modal-dialog m-0 w-100"
              style={{ maxWidth: "700px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg overflow-hidden" style={{ background: "rgba(30, 41, 59, 0.95)", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="modal-header border-0 px-4 pt-4 pb-0">
                  <h5 className="modal-title fw-bold text-white d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: "40px", height: "40px", background: "rgba(14, 165, 233, 0.15)", color: "#38bdf8" }}>
                      <Upload size={20} />
                    </div>
                    Import Contacts
                  </h5>
                  <button type="button" className="btn-close btn-close-white opacity-50" onClick={() => setShowImportModal(false)}></button>
                </div>
                <div className="modal-body p-4">
                  <div className="d-flex justify-content-center mb-5">
                    <div className="bg-dark rounded-pill p-1 d-inline-flex" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
                      <button
                        className={`btn rounded-pill px-4 py-2 border-0 fw-medium transition-all ${importTab === "csv" ? "btn-primary text-white shadow-sm" : "text-muted bg-transparent"}`}
                        onClick={() => setImportTab("csv")}
                        style={importTab === "csv" ? { background: "linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)" } : {}}
                      >
                        <FileText size={16} className="me-2" /> CSV File
                      </button>
                      <button
                        className={`btn rounded-pill px-4 py-2 border-0 fw-medium transition-all ${importTab === "sheets" ? "btn-success text-white shadow-sm" : "text-muted bg-transparent"}`}
                        onClick={() => setImportTab("sheets")}
                        style={importTab === "sheets" ? { background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" } : {}}
                      >
                        <Download size={16} className="me-2" /> Google Sheets
                      </button>
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    {importTab === "csv" ? (
                      <motion.div key="csv" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="text-center">
                        <div className="p-5 rounded-4 mb-4" style={{ background: "rgba(255,255,255,0.02)", border: "2px dashed rgba(255,255,255,0.15)", transition: "all 0.2s" }}>
                          <FileText size={48} className="text-primary opacity-50 mb-3" />
                          <h6 className="fw-semibold text-white mb-2">Upload your CSV file</h6>
                          <p className="text-muted small mb-4">Must contain <span className="text-white">UserEmail</span>. Names and categories are optional.</p>
                          
                          <input type="file" accept=".csv" className="d-none" id="csvUpload" onChange={handleCsvFileChange} disabled={importLoading} />
                          <label htmlFor="csvUpload" className="btn btn-primary rounded-pill px-5 py-2 shadow-sm" style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", border: "none", cursor: "pointer" }}>
                            {importLoading ? <><Loader2 className="animate-spin me-2 d-inline" size={18} />Uploading...</> : "Browse Files"}
                          </label>
                        </div>
                        <button onClick={downloadSampleCSV} className="btn btn-link text-info text-decoration-none small">
                          <Download size={14} className="me-1" /> Download Sample CSV
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div key="sheets" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                        <div className="mb-4">
                          <label className="form-label text-muted small text-uppercase fw-semibold mb-2">Google Sheets Public Link</label>
                          <div className="input-group">
                            <span className="input-group-text border-0" style={{ background: "rgba(15, 23, 42, 0.5)", color: "#94a3b8" }}><Link size={18} /></span>
                            <input
                              type="text"
                              className="form-control shadow-none border-0"
                              style={{ background: "rgba(15, 23, 42, 0.5)", color: "#f8fafc" }}
                              placeholder="https://docs.google.com/spreadsheets/d/..."
                              value={googleSheetLink}
                              onChange={(e) => setGoogleSheetLink(e.target.value)}
                              disabled={importLoading}
                            />
                            <button className="btn btn-success px-4 fw-medium border-0" style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }} onClick={handleGoogleSheetsImport} disabled={importLoading}>
                              {importLoading ? <Loader2 size={18} className="animate-spin" /> : "Import Data"}
                            </button>
                          </div>
                        </div>

                        <div className="p-3 rounded-4 d-flex align-items-start" style={{ background: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                          <AlertCircle className="text-success me-3 flex-shrink-0 mt-1" size={20} />
                          <div>
                            <h6 className="fw-semibold text-success mb-1" style={{ fontSize: "0.9rem" }}>How to prepare your sheet</h6>
                            <ol className="text-success text-opacity-75 small mb-0 ps-3" style={{ lineHeight: "1.6" }}>
                              <li>Include a column named <b>UserEmail</b> (required)</li>
                              <li>Click <b>Share</b> in Google Sheets top right corner</li>
                              <li>Change General Access to <b>"Anyone with the link"</b></li>
                              <li>Copy link and paste above</li>
                            </ol>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-4 p-3 rounded-4" style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.05)" }}>
                    <div className="d-flex align-items-center mb-3">
                      <CheckCircle2 size={16} className="text-info me-2" />
                      <span className="text-muted small text-uppercase fw-semibold">Format Guide</span>
                    </div>
                    <div className="row g-2 text-center">
                      <div className="col-4"><div className="p-2 rounded" style={{ background: "rgba(255,255,255,0.03)" }}><code className="text-info">UserEmail</code><div className="small text-muted mt-1" style={{ fontSize: "0.7rem" }}>Required</div></div></div>
                      <div className="col-4"><div className="p-2 rounded" style={{ background: "rgba(255,255,255,0.03)" }}><code className="text-white">userName</code><div className="small text-muted mt-1" style={{ fontSize: "0.7rem" }}>Optional</div></div></div>
                      <div className="col-4"><div className="p-2 rounded" style={{ background: "rgba(255,255,255,0.03)" }}><code className="text-white">category</code><div className="small text-muted mt-1" style={{ fontSize: "0.7rem" }}>Optional</div></div></div>
                    </div>
                    <p className="small text-muted mt-3 mb-0 text-center" style={{ fontSize: "0.75rem" }}>Any extra columns will be automatically added as <b>Custom Fields</b>.</p>
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4 pt-0">
                  <button className="btn btn-outline-light rounded-pill px-4 w-100" style={{ borderColor: "rgba(255,255,255,0.1)", color: "#94a3b8" }} onClick={() => setShowImportModal(false)}>Close</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default UserEmailTemplateManager;

// Add modern premium dark styles for the manager component
const style = document.createElement("style");
style.textContent = `
  /* Global Component Scoped Styles */
  #premium-dark-manager {
    color: #f3f4f6;
  }
  
  /* Cards */
  #premium-dark-manager .card {
    background: #111827 !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    border-radius: 1.25rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
    overflow: hidden;
  }
  #premium-dark-manager .card-header {
    background: linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    color: #fff !important;
  }

  /* Modals */
  #premium-dark-manager .modal-content {
    background: #111827 !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 1.25rem !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
    color: #f3f4f6 !important;
  }
  #premium-dark-manager .modal-header {
    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    color: #fff !important;
  }
  #premium-dark-manager .modal-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.05) !important;
    background: rgba(255, 255, 255, 0.01) !important;
  }
  #premium-dark-manager .btn-close {
    filter: invert(1) grayscale(100%) brightness(200%);
  }

  /* Table */
  #premium-dark-manager .table, 
  #premium-dark-manager .table td, 
  #premium-dark-manager .table th,
  #premium-dark-manager .table-responsive {
    color: #f3f4f6 !important;
    border-color: rgba(255, 255, 255, 0.05) !important;
  }
  #premium-dark-manager .table-light,
  #premium-dark-manager .table-light th {
    background-color: rgba(255, 255, 255, 0.03) !important;
    color: #9ca3af !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
  }
  #premium-dark-manager .table-hover tbody tr:hover td {
    background-color: rgba(255, 255, 255, 0.03) !important;
    color: #fff !important;
  }
  #premium-dark-manager .table td {
    vertical-align: middle;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    background: transparent !important;
    padding: 1rem 0.75rem !important; /* Fix for table height */
  }

  /* Pagination */
  #premium-dark-manager .pagination .page-link {
    background-color: #1f2937 !important;
    border-color: rgba(255, 255, 255, 0.05) !important;
    color: #9ca3af !important;
    transition: all 0.2s;
  }
  #premium-dark-manager .pagination .page-link:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: #fff !important;
  }
  #premium-dark-manager .pagination .page-item.active .page-link {
    background-color: #0ea5e9 !important;
    border-color: #0ea5e9 !important;
    color: #ffffff !important;
  }
  #premium-dark-manager .pagination .page-item.disabled .page-link {
    background-color: #111827 !important;
    color: #4b5563 !important;
    border-color: rgba(255, 255, 255, 0.02) !important;
  }

  /* Inputs & Forms */
  #premium-dark-manager .form-control,
  #premium-dark-manager .form-select {
    background-color: rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    color: #f3f4f6 !important;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    transition: all 0.2s ease;
  }
  #premium-dark-manager .form-control:focus,
  #premium-dark-manager .form-select:focus {
    background-color: rgba(0, 0, 0, 0.3) !important;
    border-color: rgba(56, 189, 248, 0.5) !important;
    box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.1) !important;
  }
  #premium-dark-manager .form-label {
    color: #9ca3af !important;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.025em;
  }
  #premium-dark-manager .input-group-text {
    background-color: rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    color: #9ca3af !important;
  }

  /* Dropzone & Import Styling */
  #premium-dark-manager .border-dashed {
    border: 2px dashed rgba(255,255,255,0.2) !important;
    transition: all 0.3s ease;
    background-color: rgba(255,255,255,0.02) !important;
  }
  #premium-dark-manager .border-dashed:hover {
    border-color: #38bdf8 !important;
    background-color: rgba(56,189,248,0.05) !important;
  }
  #premium-dark-manager .bg-light-subtle {
    background-color: rgba(255,255,255,0.02) !important;
  }
  #premium-dark-manager .nav-pills .nav-link {
    color: #9ca3af !important;
    border-radius: 0.5rem;
  }
  #premium-dark-manager .nav-pills .nav-link:hover {
    background-color: rgba(255,255,255,0.05);
  }
  #premium-dark-manager .nav-pills .nav-link.active {
    background-color: #0ea5e9 !important;
    color: #fff !important;
    box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.3);
  }

  /* Buttons */
  #premium-dark-manager .btn-primary {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
    border: none !important;
    color: white !important;
    box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
  }
  #premium-dark-manager .btn-outline-primary {
    color: #38bdf8 !important;
    border-color: rgba(56,189,248,0.3) !important;
    background: rgba(56,189,248,0.05) !important;
  }
  #premium-dark-manager .btn-outline-primary:hover {
    background: rgba(56,189,248,0.15) !important;
    color: #fff !important;
  }
  #premium-dark-manager .btn-success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    border: none !important;
  }
  
  /* Danger and Warning buttons in table */
  #premium-dark-manager .btn-warning {
    background: rgba(245, 158, 11, 0.15) !important;
    color: #fbbf24 !important;
    border: 1px solid rgba(245, 158, 11, 0.3) !important;
  }
  #premium-dark-manager .btn-warning:hover {
    background: rgba(245, 158, 11, 0.25) !important;
    color: #fcd34d !important;
  }
  #premium-dark-manager .btn-danger {
    background: rgba(239, 68, 68, 0.15) !important;
    color: #f87171 !important;
    border: 1px solid rgba(239, 68, 68, 0.3) !important;
  }
  #premium-dark-manager .btn-danger:hover {
    background: rgba(239, 68, 68, 0.25) !important;
    color: #fca5a5 !important;
  }

  /* Badges & Text */
  #premium-dark-manager .text-muted {
    color: #9ca3af !important;
  }
  #premium-dark-manager .bg-white {
    background: #1f2937 !important;
  }
  #premium-dark-manager .text-dark {
    color: #f3f4f6 !important;
  }
  #premium-dark-manager .badge {
    padding: 0.4em 0.8em;
    font-weight: 600;
    letter-spacing: 0.025em;
  }
  #premium-dark-manager .bg-info {
    background-color: rgba(14, 165, 233, 0.2) !important;
    color: #38bdf8 !important;
    border: 1px solid rgba(14, 165, 233, 0.3) !important;
  }
  #premium-dark-manager .bg-success {
    background-color: rgba(16, 185, 129, 0.2) !important;
    color: #34d399 !important;
    border: 1px solid rgba(16, 185, 129, 0.3) !important;
  }
  #premium-dark-manager .bg-secondary {
    background-color: rgba(156, 163, 175, 0.15) !important;
    color: #d1d5db !important;
    border: 1px solid rgba(156, 163, 175, 0.3) !important;
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .import-zone {
    cursor: pointer;
  }
`;
document.head.appendChild(style);
